import React from 'react';
import ReactInputVerification from 'react-verification-input';
import { Button, styled, Typography } from '@mui/material';
// Components
import AppCard from '@components/AppCard';
import { Api } from '@utils';
import Message from '@components/Message';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from './AuthContext';

const VerifyWrap = styled('div')<{ disable?: boolean }>(({ disable }) => ({
  '& > div': {
    width: '100%',
    maxWidth: 320,
    margin: '0 auto',
  },

  '& .container': {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    opacity: disable ? 0.5 : 1,
  },

  '& .character': {
    fontSize: 24,
    borderRadius: 8,
    fontWeight: 500,
    border: '1px solid #e4e2f5',
    color: '#272729',
    backgroundColor: '#e4e2f5',
    boxShadow: '0 2px 0 #e4e2f5',
    transition: 'all 0.2s ease-in-out',
  },

  '& .character--inactive': {
    backgroundColor: '#e4e2f5',
  },

  '& .character--selected': {
    outline: 'none',
    backgroundColor: '#fff',
    boxShadow: '0 2px 0 #e4e2f5',
  },
}));

const ResendCode = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  textAlign: 'center',
  marginTop: theme.spacing(3),

  '& .MuiButton-root': {
    padding: 0,
    minWidth: 'auto',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    opacity: 0.8,
    fontSize: 14,
    transition: 'all 0.3s ease-in-out',

    '&:hover': {
      opacity: 1,
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
    },
  },
}));

const VerifyForm = () => {
  const [otp, setOtp] = React.useState('');
  const [timer, setTimer] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);

  const { setIsVerified } = useAuthContext();

  const navigate = useNavigate();

  // Get query params by react-router-dom
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  // Redirect after 1 seconds
  React.useEffect(() => {
    if (success) {
      setTimeout(() => {
        setIsVerified(true);
        navigate('/');
      }, 1000);
    }
  }, [success]);

  // Resend Handler
  const handleResend = () => {
    setOtp('');
    setError('');
    setTimer(30);

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    setTimeout(() => {
      clearInterval(interval);
    }, 30000);

    const _resend = async () => {
      try {
        const { data } = await Api.post('/resend', { email });
        if (data.success === true) {
          setError('');
        }
      } catch (error: any) {
        setError(error);
      }
    };

    _resend();
  };

  // Handle Verify
  const handleVerify = async (otp: string) => {
    setLoading(true);

    try {
      const { data } = await Api.post('/verify', { otp, email });
      if (data.success === true) {
        setSuccess(true);
        setLoading(false);
      }
    } catch (error) {
      setError('Invalid code');
      setLoading(false);
      setSuccess(false);
    }
  };

  return (
    <AppCard>
      <Typography variant="h5" gutterBottom textAlign="center">
        Verify your account
      </Typography>

      <Typography variant="body2" mb={3} textAlign="center">
        We sent you a 6-digit code to your email address. Please enter it below.
      </Typography>

      <VerifyWrap disable={loading}>
        <ReactInputVerification
          classNames={{
            container: 'container',
            character: 'character',
            characterInactive: 'character--inactive',
            characterSelected: 'character--selected',
          }}
          length={6}
          validChars="0-9"
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'one-time-code',
            disabled: loading,
          }}
          placeholder="_"
          value={otp}
          onChange={(value) => setOtp(value)}
          onComplete={(value) => handleVerify(value)}
        />
      </VerifyWrap>

      {error && <Message sx={{ mt: 3 }} type="error" title={error} />}
      {!error && success && (
        <Message
          sx={{ mt: 3 }}
          type="success"
          title="Verified successfully! Redirecting..."
        />
      )}

      <ResendCode variant="body2">
        Didn't receive the code?{' '}
        <Button variant="text" onClick={handleResend} disabled={timer > 0}>
          Resend {timer > 0 && `(${timer})`}
        </Button>
      </ResendCode>
    </AppCard>
  );
};

export default VerifyForm;
